import { Component, DestroyRef, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  AccessOption,
  API,
  ClientCullDTO,
  CullDTO,
  CullWithAtomsDTO,
  NetworkShareDTO,
  Pagination,
  PlayerCullDTO,
} from 'src/app/services/api/api';
import { ErrorService } from 'src/app/services/error.service';
import { UtilService } from 'src/app/services/util.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { BehaviorSubject, Observable } from 'rxjs';
import { PermissionService } from '../../../services/permission.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'sb-page-network-share-edit',
  templateUrl: './network-share-edit.component.html',
  styleUrls: ['./network-share-edit.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [style({ flex: '0' }), animate('200ms 210ms linear', style({ flex: '1' }))]),
      transition(':leave', [animate('200ms 100ms linear', style({ flex: '0' }))]),
    ]),
  ],
  standalone: false,
})
export class NetworkShareEditComponent implements OnInit {
  get Name() {
    return this.form.get('Name');
  }

  get Description() {
    return this.form.get('Description');
  }

  get Bookable() {
    return this.form.get('Bookable');
  }

  get Subletable() {
    return this.form.get('Subletable');
  }

  get ShowOnlineStatus() {
    return this.form.get('ShowOnlineStatus');
  }

  get ShowPlaylist() {
    return this.form.get('ShowPlaylist');
  }

  get ShowPlayouts() {
    return this.form.get('ShowPlayouts');
  }

  get ShowLogs() {
    return this.form.get('ShowLogs');
  }

  get AccessTemplateData() {
    return this.form.get('AccessTemplateData');
  }

  get AccessBookings() {
    return this.form.get('AccessBookings');
  }

  constructor(
    private api: API,
    private activatedRoute: ActivatedRoute,
    public util: UtilService,
    private errorHandler: ErrorService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef
  ) {
    this.pagination = new BehaviorSubject<Pagination>({
      page: 1,
      count: 500,
      pages: -1,
      resultcount: -1,
    });
    this.precondition = null;
    this.pickedPosition = null;
  }

  loading: boolean = true;
  shareId: number;
  debounceTimer: ReturnType<typeof setTimeout> = null;
  share: NetworkShareDTO;
  editMode: boolean;

  form = new UntypedFormGroup({
    Name: new UntypedFormControl('', [Validators.required]),
    Description: new UntypedFormControl('', []),
    Bookable: new UntypedFormControl(-1),
    Subletable: new UntypedFormControl(-1),
    ShowOnlineStatus: new UntypedFormControl(-1),
    ShowPlaylist: new UntypedFormControl(-1),
    ShowPlayouts: new UntypedFormControl(-1),
    ShowLogs: new UntypedFormControl(-1),
    AccessTemplateData: new UntypedFormControl(AccessOption.None),
    AccessBookings: new UntypedFormControl(AccessOption.None),

    /*Level: new FormControl('', [
        Validators.pattern('^[0-9]*$'),
        Validators.required
      ]),*/
    /*Priority: new FormControl('', [
        Validators.pattern('^[0-9]*$'),
        Validators.required
      ]),*/
  });

  shares: NetworkShareDTO[];
  precondition: NetworkShareDTO;
  preconditions: NetworkShareDTO[];
  pickedPosition: NetworkShareDTO;
  pagination: BehaviorSubject<Pagination>;

  clientCull: ClientCullDTO;
  playerCull: PlayerCullDTO;

  wizardState: string = '';

  protected readonly AccessOption = AccessOption;

  ngOnInit(): void {
    this.loading = true;

    this.activatedRoute.params.subscribe(params => {
      this.shareId = +params.networkshareID;
      this.api
        .ss_nm_NetworkShare_retrieveAll({}, this.pagination)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe((shares: NetworkShareDTO[]) => {
          this.pickedPosition = this.shareId ? shares.filter(x => x.id === this.shareId)[0] : null;
          this.shares = this.shareId ? shares.filter(x => x?.id?.toString() !== this.shareId?.toString()) : shares;
          if (this.shareId) {
            this.editMode = true;

            this.requestDebounced();
          } else {
            this.editMode = false;
            this.share = this.api.createDTO('NetworkShareDTO');
            this.unsetLoading();
          }
        });
    });
  }

  requestDebounced() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.refreshFromServer();
    }, 500);
  }

  refreshFromServer() {
    this.api
      .ss_nm_NetworkShare_retrieveById(this.shareId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.unsetLoading();
        })
      )
      .subscribe({
        next: (shareData: NetworkShareDTO) => {
          this.share = shareData;
          this.precondition = this.share.precondition ? this.share.precondition : null;
          this.refreshFormFromDTO();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  refreshFormFromDTO() {
    this.Name.setValue(this.share.name);
    this.Description.setValue(this.share.description);

    this.Bookable.setValue(this.booleanToNumber(this.share.bookable));
    this.Subletable.setValue(this.booleanToNumber(this.share.subletable));
    this.ShowOnlineStatus.setValue(this.booleanToNumber(this.share.showOnlineStatus));
    this.ShowPlaylist.setValue(this.booleanToNumber(this.share.showPlaylist));
    this.ShowPlayouts.setValue(this.booleanToNumber(this.share.showPlayouts));
    this.ShowLogs.setValue(this.booleanToNumber(this.share.showLogs));

    this.AccessBookings.setValue(this.share.bookings ? this.share.bookings : AccessOption.None);
    this.AccessTemplateData.setValue(this.share.templateData ? this.share.templateData : AccessOption.None);

    this.clientCull = this.share.clientCull;
    this.playerCull = this.share.playerCull;
  }

  refreshDTOFromForm() {
    this.share.name = this.Name.value;
    this.share.description = this.Description.value;

    this.share.bookable = this.numberToBoolean(this.Bookable.value);
    this.share.subletable = this.numberToBoolean(this.Subletable.value);
    this.share.showOnlineStatus = this.numberToBoolean(this.ShowOnlineStatus.value);
    this.share.showPlaylist = this.numberToBoolean(this.ShowPlaylist.value);
    this.share.showPlayouts = this.numberToBoolean(this.ShowPlayouts.value);
    this.share.showLogs = this.numberToBoolean(this.ShowLogs.value);

    this.share.bookings = AccessOption[this.AccessBookings.value];
    this.share.templateData = AccessOption[this.AccessTemplateData.value];

    this.share.clientCull = this.clientCull;
    this.share.playerCull = this.playerCull;
  }

  /*setValue(key, value) {
    if (!(this.share === undefined || this.share === null)) {
      switch (key) {
        default:
          break;
      }
      this.refreshDTOFromForm();
    }
  }*/

  filterSubmitSuccess(cull: CullWithAtomsDTO) {
    if (!cull) {
      this.changeWizardState('');
    } else {
      switch (cull.type) {
        case 'ClientCull':
          this.selectFilter(cull as ClientCullDTO);
          break;
        case 'PlayerCull':
          this.selectFilter(cull as PlayerCullDTO);
          break;
      }
    }
  }

  selectFilter(cull: CullDTO) {
    this.setCull(cull);
    this.changeWizardState('');
  }

  setCull(cull: CullDTO) {
    switch (cull.type) {
      case 'PlayerCull':
        this.playerCull = cull as PlayerCullDTO;
        if (!cull.id) {
          this.playerCull = null;
        }
        break;
      case 'ClientCull':
        this.clientCull = cull as ClientCullDTO;
        if (!cull.id) {
          this.clientCull = null;
        }
        break;
    }
  }

  sliceCull(e: MouseEvent, type: string) {
    e.stopPropagation();
    e.preventDefault();
    switch (type) {
      case 'ClientCull': {
        if (this.clientCull) {
          this.clientCull = null;
        }
        break;
      }
      case 'PlayerCull': {
        if (this.playerCull) {
          this.playerCull = null;
        }
        break;
      }
    }
    this.changeWizardState('');
  }

  changeWizardState(state: string) {
    this.wizardState = state;
  }

  submit() {
    this.share.precondition = this.precondition;
    const preconditionsCheck = this.preconditions.length > 0 ? this.preconditions[this.preconditions.length - 1].priority + 1 : 0;
    this.share.priority = this.pickedPosition ? this.pickedPosition.priority : preconditionsCheck;
    this.loading = true;
    this.refreshDTOFromForm();

    let obs: Observable<NetworkShareDTO>;

    if (this.editMode) {
      obs = this.api.ss_nm_NetworkShare_editNetworkShare(this.share);
    } else {
      obs = this.api.ss_nm_NetworkShare_createNetworkShare(this.share);
    }

    if (obs) {
      obs
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.unsetLoading();
          })
        )
        .subscribe({
          next: data => {
            this.share = data;
            this.refreshFormFromDTO();
            this.util.returnToNavigation(this.util.RoutingID.networkShare);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  cancel() {
    this.util.returnToNavigation(this.util.RoutingID.networkShare);
  }

  unsetLoading() {
    setTimeout(() => {
      this.retrieveHierarchy();
      this.loading = false;
    }, 500);
  }

  updatePosition($event: NetworkShareDTO) {
    this.pickedPosition = $event;
  }

  updatePrecondition($event: NetworkShareDTO) {
    this.precondition = $event;
    this.retrieveHierarchy();
  }

  private retrieveHierarchy() {
    this.api
      .ss_nm_NetworkShare_retrieveHierarchy(
        {
          precondition: this.precondition?.id ? this.precondition.id : null,
        },
        this.pagination
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((preconditions: NetworkShareDTO[]) => {
        const helper = preconditions.filter(x => x.id?.toString() === this.shareId?.toString())[0];
        this.pickedPosition = helper || null;
        this.preconditions = preconditions;
      });
  }

  private booleanToNumber(bool: boolean | null): number {
    if (bool === true) {
      return 1;
    } else if (bool === false) {
      return 0;
    }
    return -1;
  }

  private numberToBoolean(num: number): boolean | null {
    if (num === 0) {
      return false;
    } else if (num === 1) {
      return true;
    }
    return null;
  }
}
